import React from 'react';
import styled from 'styled-components';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTranslation } from 'react-i18next';

export interface Station {
  name: string;
  country: string;
  lat: number;
  lng: number;
  punctuality: number;
  avgDelay: number;
}

interface StationMapProps {
  stations: Station[];
  center?: [number, number];
  zoom?: number;
  mapboxAccessToken?: string;
}

const colors = {
  emerald400: '#34D399',
  emerald500: '#10B981',
  blue400: '#60A5FA',
  blue500: '#3B82F6',
  yellow400: '#FACC15',
  yellow500: '#EAB308',
  orange400: '#FB923C',
  orange500: '#F97316',
  red400: '#F87171',
  red500: '#EF4444',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate700: '#334155',
};

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 500px;
  height: 500px;
  border-radius: 1rem;
  overflow: hidden;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid ${colors.slate700};
  margin-top: 2rem;
  /* Prevent flickering during scroll */
  isolation: isolate;
  contain: layout style paint;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  
  @media (min-width: 768px) {
    min-height: 600px;
    height: 600px;
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  isolation: isolate;
  contain: layout style paint;
  z-index: 1;
  
  .mapboxgl-map {
    border-radius: 1rem;
    width: 100%;
    height: 100%;
    /* Prevent flickering */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
  
  .mapboxgl-canvas {
    /* Keep this light—aggressive image-rendering / transforms can cause flicker */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
  
  .mapboxgl-ctrl-bottom-right {
    bottom: 10px;
    right: 10px;
  }
  
  .mapboxgl-ctrl-top-right {
    top: 10px;
    right: 10px;
  }
  
  .mapboxgl-ctrl button {
    background-color: rgba(30, 41, 59, 0.9);
    border: 1px solid ${colors.slate700};
    color: white;
    
    &:hover {
      background-color: rgba(30, 41, 59, 1);
    }
    
    svg {
      fill: white;
    }
  }
  
  .mapboxgl-ctrl-icon {
    filter: brightness(0) invert(1);
  }
  
  /* Hide the compass/rotation control */
  .mapboxgl-ctrl-compass {
    display: none !important;
  }
`;

// Marker container is now handled by Mapbox, but we keep this for tooltip positioning
const TooltipContainer = styled.div<{ x: number; y: number }>`
  position: absolute;
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  transform: translate(-50%, -100%);
  pointer-events: none;
  z-index: 1000;
`;


const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${colors.slate700};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 10;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: ${colors.slate300};
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: rgba(30, 41, 59, 0.95);
  }
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: white;
`;

const TooltipText = styled.div`
  font-size: 0.75rem;
  color: ${colors.slate400};
`;

// Toggle container
const ToggleContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  background: rgba(30, 41, 59, 0.9);
  backdrop-filter: blur(8px);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${colors.slate700};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: ${colors.slate300};
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
`;

const ToggleSwitch = styled.input.attrs({ type: 'checkbox' })`
  width: 2.5rem;
  height: 1.25rem;
  appearance: none;
  background: ${colors.slate700};
  border-radius: 9999px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &::before {
    content: '';
    position: absolute;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: white;
    top: 0.125rem;
    left: 0.125rem;
    transition: transform 0.2s ease;
  }
  
  &:checked {
    background: ${colors.blue500};
    
    &::before {
      transform: translateX(1.25rem);
    }
  }
  
  &:hover:not(:checked) {
    background: ${colors.slate400};
  }
  
  &:hover:checked {
    background: ${colors.blue400};
  }
`;

// Get color based on punctuality - gradient from red (low) to green (high)
// Adjusted scale to show more red dots (red covers 0-75%, orange/yellow 75-90%, green 90-100%)
const getPunctualityColor = (punctuality: number): string => {
  // Clamp punctuality between 0 and 100
  const clamped = Math.max(0, Math.min(100, punctuality));
  
  // Interpolate between red (0-75%) -> orange/yellow (75-90%) -> green (90-100%)
  // Using HSL for smoother color transitions
  let hue: number;
  let saturation: number;
  let lightness: number;
  
  if (clamped < 50) {
    // Red range: 0% to 75% - stays mostly red with slight variation
    const t = clamped / 50;
    hue = 0 + (15 - 0) * t; // Red (0°) to slightly orange-red (15°)
    saturation = 85 + (90 - 85) * t;
    lightness = 45 + (50 - 45) * t;
  } else if (clamped < 80) {
    // Orange/yellow range: 75% to 90%
    const t = (clamped - 50) / 15;
    hue = 15 + (60 - 15) * t; // Orange-red (15°) to yellow (60°)
    saturation = 90 + (85 - 90) * t;
    lightness = 50 + (55 - 50) * t;
  } else {
    // Green range: 80% to 100%
    const t = (clamped - 80) / 10;
    hue = 60 + (120 - 60) * t; // Yellow (60°) to green (120°)
    saturation = 85 + (70 - 85) * t;
    lightness = 55 + (50 - 55) * t;
  }
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Colorblind-friendly color function
// Bad (low punctuality): Deep orange → Orange
// Medium: Yellow/cream
// Good (high punctuality): Cyan → Deep blue
const getPunctualityColorColorblind = (punctuality: number): string => {
  // Clamp punctuality between 0 and 100
  const clamped = Math.max(0, Math.min(100, punctuality));
  
  let hue: number;
  let saturation: number;
  let lightness: number;
  
  if (clamped < 50) {
    // Bad: Deep orange (20°) to Orange (35°)
    const t = clamped / 50;
    hue = 20 + (35 - 20) * t;
    saturation = 85 + (90 - 85) * t;
    lightness = 50 + (55 - 50) * t;
  } else if (clamped < 80) {
    // Medium: Yellow/cream (50° to 60°)
    const t = (clamped - 50) / 30;
    hue = 50 + (60 - 50) * t;
    saturation = 70 + (60 - 70) * t; // Lower saturation for cream
    lightness = 55 + (70 - 55) * t; // Lighter for cream
  } else {
    // Good: Cyan (180°) to Deep blue (240°)
    const t = (clamped - 80) / 20;
    hue = 180 + (240 - 180) * t;
    saturation = 80 + (85 - 80) * t;
    lightness = 60 + (50 - 60) * t;
  }
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Dark style ID from Mapbox - using 'dark-v11' for dark theme
const DARK_STYLE = 'mapbox://styles/mapbox/dark-v11';

const StationMap: React.FC<StationMapProps> = ({ 
  stations, 
  center = [10.5, 50.5], // Center of Europe [lng, lat] for Mapbox
  zoom = 5,
  mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''
}) => {
  const { t } = useTranslation();
  const [hoveredStation, setHoveredStation] = React.useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [colorblindMode, setColorblindMode] = React.useState(false);
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<mapboxgl.Map | null>(null);
  const markersRef = React.useRef<mapboxgl.Marker[]>([]);
  const markersByStationNameRef = React.useRef<Map<string, mapboxgl.Marker>>(new Map());
  const initStartedRef = React.useRef(false);

  React.useEffect(() => {
    // Prevent multiple initializations - if map exists or init already started, bail out
    if (!mapContainerRef.current || mapRef.current || initStartedRef.current) return;
    
    // Mark initialization as started immediately to prevent race conditions
    initStartedRef.current = true;

    // Check for access token
    if (!mapboxAccessToken) {
      console.warn('Mapbox access token not provided. Please set REACT_APP_MAPBOX_ACCESS_TOKEN environment variable.');
      return;
    }

    // Set Mapbox access token
    mapboxgl.accessToken = mapboxAccessToken;

    let cancelled = false;

    const tryInit = (attempt: number) => {
      if (cancelled || !mapContainerRef.current || mapRef.current || !initStartedRef.current) return;

      // Ensure container has dimensions (can be 0 on first paint after refresh/layout)
      if (mapContainerRef.current.offsetWidth === 0 || mapContainerRef.current.offsetHeight === 0) {
        if (attempt < 20) {
          setTimeout(() => tryInit(attempt + 1), 50);
        } else {
          console.warn('Map container still has no dimensions after retries');
        }
        return;
      }

    try {
      // Initialize map
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: DARK_STYLE,
        center: center,
        zoom: zoom,
        attributionControl: false
      });

      // Add navigation controls
      map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

      // Apply optimizations to prevent flickering
      map.on('load', () => {
          setMapLoaded(true);
        const canvas = map.getCanvas();
        if (canvas) {
          canvas.style.transform = 'translateZ(0)';
          canvas.style.webkitTransform = 'translateZ(0)';
          canvas.style.imageRendering = 'crisp-edges';
        }
        // Trigger resize to ensure map renders correctly
        map.resize();
      });

      // Handle errors
      map.on('error', (e) => {
        console.error('Map error:', e);
      });

      mapRef.current = map;
      
      // Resize map after a short delay to ensure container is fully rendered
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      }, 100);
      } catch (error) {
        console.error('Failed to initialize map:', error);
      }
    };

    tryInit(0);

      // Cleanup
      return () => {
      cancelled = true;
      initStartedRef.current = false;
      setMapLoaded(false);
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
      markersByStationNameRef.current.clear();
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
  }, [center, zoom, mapboxAccessToken]);

  // Stable content signature for stations to avoid tearing down/recreating markers
  // on every render when parent passes a new `stations` array reference.
  const stationsSignature = React.useMemo(() => {
    return stations
      .map((s) => `${s.name}|${s.country}|${s.lat}|${s.lng}|${s.punctuality}|${s.avgDelay}`)
      .join('||');
  }, [stations]);

  // Update markers when stations change
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    // Wait for map to be loaded before adding markers
    const addMarkers = () => {
      // Remove existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      markersByStationNameRef.current.clear();

      // Add new markers
      stations.forEach((station) => {
        const color = colorblindMode 
          ? getPunctualityColorColorblind(station.punctuality)
          : getPunctualityColor(station.punctuality);
        
        // Create wrapper element (Mapbox will position this)
        const wrapper = document.createElement('div');
        // Important: don't force `position: absolute` here. Mapbox positions markers
        // by applying transforms to the element; overriding positioning can cause
        // markers to "disappear" during map moves in some browsers.
        wrapper.style.transformOrigin = 'center center';
        wrapper.style.willChange = 'transform';
        wrapper.style.pointerEvents = 'none';
        
        // Create marker element (inside wrapper for hover effects)
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.style.width = '12px';
        el.style.height = '12px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = color;
        el.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.4)';
        el.style.cursor = 'pointer';
        el.style.transition = 'transform 0.15s ease-out';
        el.style.transformOrigin = 'center center';
        el.style.pointerEvents = 'auto';
        el.style.position = 'relative';
        
        wrapper.appendChild(el);
        
        // Create marker with optimized settings for smooth movement
        const marker = new mapboxgl.Marker({
          element: wrapper,
          anchor: 'center',
          draggable: false
        })
          .setLngLat([station.lng, station.lat])
          .addTo(mapRef.current!);

        // Store station data on marker element for tooltip
        (el as any).stationData = station;

        // Add hover effects - scale only affects the inner element, not Mapbox positioning
        el.addEventListener('mouseenter', () => {
          setHoveredStation(station.name);
          el.style.transform = 'scale(1.3)';
          el.style.zIndex = '10';
        });

        el.addEventListener('mouseleave', () => {
          setHoveredStation(null);
          el.style.transform = 'scale(1)';
          el.style.zIndex = '1';
        });

        markersRef.current.push(marker);
        markersByStationNameRef.current.set(station.name, marker);
      });
    };

    if (mapRef.current.loaded()) {
      addMarkers();
    } else {
      mapRef.current.once('load', addMarkers);
    }
  }, [stationsSignature, mapLoaded, colorblindMode]);

  // Update tooltip position when map moves or station is hovered
  const [tooltipPosition, setTooltipPosition] = React.useState<{ x: number; y: number } | null>(null);

  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded || !hoveredStation) {
      setTooltipPosition(null);
      return;
    }

    const updateTooltipPosition = () => {
      const marker = markersByStationNameRef.current.get(hoveredStation);
      if (!marker || !mapRef.current) return;

      const lngLat = marker.getLngLat();
      const point = mapRef.current.project(lngLat);
      setTooltipPosition({ x: point.x, y: point.y });
    };

    updateTooltipPosition();
    mapRef.current.on('move', updateTooltipPosition);
    mapRef.current.on('zoom', updateTooltipPosition);

    return () => {
      if (mapRef.current) {
        mapRef.current.off('move', updateTooltipPosition);
        mapRef.current.off('zoom', updateTooltipPosition);
      }
    };
  }, [hoveredStation, stationsSignature, mapLoaded]);

  return (
    <MapContainer>
      <MapWrapper ref={mapContainerRef} />
      <ToggleContainer>
        <ToggleLabel>
          <ToggleSwitch
            checked={colorblindMode}
            onChange={(e) => setColorblindMode(e.target.checked)}
            aria-label={t('wrapped2025.stationMap.colorblindMode')}
          />
          <span>{t('wrapped2025.stationMap.colorblindMode')}</span>
        </ToggleLabel>
      </ToggleContainer>
      {hoveredStation && tooltipPosition && (() => {
        const station = stations.find(s => s.name === hoveredStation);
        if (!station) return null;
        
        return (
          <TooltipContainer x={tooltipPosition.x} y={tooltipPosition.y - 10}>
            <Tooltip visible={true}>
              <TooltipTitle>{station.name}</TooltipTitle>
              <TooltipText>{station.country}</TooltipText>
              <TooltipText>{t('wrapped2025.stationMap.punctuality')} {station.punctuality.toFixed(1)}%</TooltipText>
              <TooltipText>{t('wrapped2025.stationMap.avgDelay')} {station.avgDelay.toFixed(1)} {t('wrapped2025.stationMap.min')}</TooltipText>
            </Tooltip>
          </TooltipContainer>
        );
      })()}
    </MapContainer>
  );
};

// Memoize to prevent unnecessary re-renders when parent re-renders
export default React.memo(StationMap);

